/**
 * Build TSC/TSPL command bytes for direct RAW label printing (desktop Electron only).
 * Matches BarcodeGeneratePage field positions in mm; coordinates include label margins.
 *
 * v1: single column (roll / one label per print). Multi-column sheet → use browser print.
 */

const MM_PER_IN = 25.4;

/** UTF-8 bytes; works in browsers and Node/Jest (no TextEncoder in older Node test env). */
function encodeUtf8(string) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(string);
  }
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(string, 'utf8'));
  }
  throw new Error('UTF-8 encoding is not available');
}

function normalizeDpi(d) {
  return Number(d) === 300 ? 300 : 203;
}

/**
 * @param {number} mm
 * @param {number} dpi
 * @returns {number} dot position (integer)
 */
export function mmToDots(mm, dpi) {
  return Math.max(0, Math.round((Number(mm) * dpi) / MM_PER_IN));
}

/**
 * @param {string} s
 * @returns {string}
 */
function escapeTsplData(s) {
  if (s == null) return '';
  return String(s).replace(/"/g, "'").replace(/\r\n|\r|\n/g, ' ').replace(/\0/g, '');
}

/**
 * Map UI font size (pt) to TSPL x/y scale multipliers (font "3" ≈ 16×24 dot cell).
 * @param {number} fontPt
 * @returns {number} 1–3
 */
function fontScaleFromPt(fontPt) {
  const n = Number(fontPt);
  if (Number.isNaN(n) || n <= 8) return 1;
  if (n <= 10) return 2;
  return 3;
}

/**
 * Built-in bitmap font: "2" 12×20, "3" 16×24 (commonly used).
 * @param {number} fontPt
 * @returns {string} font id
 */
function fontIdFromPt(fontPt) {
  const n = Number(fontPt);
  if (Number.isNaN(n) || n <= 7) return '2';
  return '3';
}

/**
 * Rough estimate for horizontal placement (centre/right) in dots.
 * @param {string} text
 * @param {number} xMul
 * @returns {number}
 */
function estimateTextWidthDots(text, xMul) {
  const t = String(text);
  return Math.max(0, Math.round(t.length * 9 * xMul));
}

/**
 * @param {object} item field layout item
 * @param {string} text
 * @param {string} align left|centre|center|right
 * @param {number} mLeftDot
 * @param {number} mTopDot
 * @param {number} dpi
 * @param {number} xMul
 * @param {number} yMul
 * @returns {{ x: number, y: number }}
 */
function textOriginDots(item, text, align, mLeftDot, mTopDot, dpi, xMul) {
  const leftMm = item.left != null ? Number(item.left) : 4;
  const topMm = item.top != null ? Number(item.top) : 2;
  const widthMm = item.width != null ? Number(item.width) : 35;

  const leftDot = mLeftDot + mmToDots(leftMm, dpi);
  const topDot = mTopDot + mmToDots(topMm, dpi);
  const boxWd = mmToDots(widthMm, dpi);

  const a = (align || 'left').toLowerCase();
  const est = estimateTextWidthDots(text, xMul);
  if (a === 'right') {
    return { x: Math.max(0, leftDot + boxWd - est), y: topDot };
  }
  if (a === 'centre' || a === 'center') {
    return { x: Math.max(0, leftDot + Math.floor((boxWd - est) / 2)), y: topDot };
  }
  return { x: leftDot, y: topDot };
}

/**
 * One label: CLS, elements, PRINT 1,1.
 * @param {object} p
 * @param {object} p.settings label settings (same shape as BarcodeGeneratePage)
 * @param {Array<object>} p.fieldLayout
 * @param {object} p.product
 * @param {function} p.getFieldValue
 * @param {number} p.labelDpi
 * @returns {string[]}
 */
function buildOneLabelCommands(p) {
  const { settings, fieldLayout, product, getFieldValue, labelDpi: dpi } = p;
  const codeType = (settings.codeType || 'BARCODE').toUpperCase();
  const mLeft = Number(settings.marginLeftMm) || 0;
  const mTop = Number(settings.marginTopMm) || 0;
  const mLeftDot = mmToDots(mLeft, dpi);
  const mTopDot = mmToDots(mTop, dpi);
  const baseFontPt = settings.fontSizePt ?? 8;
  const barcodeW = Math.max(1, Number(settings.barcodeWidthMm) || 25);
  const barcodeH = Math.max(1, Number(settings.barcodeHeightMm) || 15);

  const lines = ['CLS'];
  const qrField = fieldLayout.find((f) => f.fieldKey === 'QR' && f.show !== false);
  const showCodeImage =
    (codeType === 'QR' || codeType === 'BARCODE') && !!qrField;

  const dataStr = escapeTsplData(product.barcode != null && product.barcode !== '' ? String(product.barcode) : String(product.id));

  if (showCodeImage && dataStr) {
    const ql = Number(qrField?.left) || 0;
    const qt = Number(qrField?.top) || 0;
    const qw = qrField?.width != null ? Number(qrField.width) : barcodeW;
    const qh = qrField?.height != null ? Number(qrField.height) : barcodeH;
    const x = mLeftDot + mmToDots(ql, dpi);
    const y = mTopDot + mmToDots(qt, dpi);
    if (codeType === 'QR') {
      const side = Math.max(mmToDots(Math.min(qw, qh), dpi), 20);
      const cell = Math.max(2, Math.min(10, Math.floor(side / 25)));
      lines.push(`QRCODE ${x},${y},M,${cell},A,0,"${dataStr}"`);
    } else {
      const h = Math.max(20, Math.min(320, mmToDots(qh, dpi)));
      const narrow = 2;
      const wide = h > 120 ? 4 : 3;
      lines.push(`BARCODE ${x},${y},"128",${h},0,0,${narrow},${wide},"${dataStr}"`);
    }
  }

  const textItems = fieldLayout.filter((f) => f.fieldKey !== 'QR' && f.show !== false);

  for (const item of textItems) {
    const key = item.fieldKey;
    const text = getFieldValue(product, key);
    if (text == null || String(text).trim() === '') continue;

    const itemFontPt = item.fontSize ?? baseFontPt;
    const yMul = fontScaleFromPt(itemFontPt);
    const xMul = yMul;
    const font = fontIdFromPt(itemFontPt);
    const align = (item.alignment || 'Left').toLowerCase() === 'centre' || (item.alignment || '').toLowerCase() === 'center'
      ? 'centre'
      : (item.alignment || 'Left').toLowerCase() === 'right'
        ? 'right'
        : 'left';

    if (key === 'MRP' || key === 'mrp') {
      const topBase = item.top != null ? Number(item.top) : 2;
      const vM = textOriginDots({ ...item, top: topBase }, 'MRP', align, mLeftDot, mTopDot, dpi, xMul);
      const vVal = textOriginDots(
        { ...item, top: topBase + 3.5 },
        String(text),
        align,
        mLeftDot,
        mTopDot,
        dpi,
        xMul
      );
      lines.push(`TEXT ${vM.x},${vM.y},"${font}",0,${xMul},${yMul},"MRP"`);
      lines.push(`TEXT ${vVal.x},${vVal.y},"${font}",0,${xMul},${yMul},"${escapeTsplData(text)}"`);
      continue;
    }

    const { x, y } = textOriginDots(item, String(text), align, mLeftDot, mTopDot, dpi, xMul);
    lines.push(`TEXT ${x},${y},"${font}",0,${xMul},${yMul},"${escapeTsplData(text)}"`);
  }

  lines.push('PRINT 1,1');
  return lines;
}

/**
 * @param {object} params
 * @param {object} params.settings
 * @param {number} [params.labelDpi]
 * @param {object[]} params.products
 * @param {function(object, string): string} params.getFieldValue
 * @param {object[]} params.fieldLayout - visible fields, sorted (same as getFieldLayout() on the page)
 * @returns {{ ok: true, buffer: Uint8Array } | { ok: false, reason: string }}
 */
export function buildTsplForLabels({ settings, labelDpi, products, getFieldValue, fieldLayout }) {
  if (!Array.isArray(products) || products.length === 0) {
    return { ok: false, reason: 'No labels to print' };
  }
  if (typeof getFieldValue !== 'function') {
    return { ok: false, reason: 'getFieldValue is required' };
  }
  if (!Array.isArray(fieldLayout)) {
    return { ok: false, reason: 'fieldLayout is required' };
  }

  const cols = Math.max(1, parseInt(settings.noOfColumns, 10) || 1);
  if (cols !== 1) {
    return { ok: false, reason: 'TSPL direct print supports single-column (roll) layout only' };
  }

  const w = Math.max(1, Number(settings.labelWidthMm) || 39);
  const h = Math.max(1, Number(settings.labelHeightMm) || 35);
  const dpi = normalizeDpi(labelDpi);
  const gapMm = Math.max(0, Number(settings.verticalGapMm) || 0);
  const gap = gapMm > 0 ? gapMm : 2;

  const header = [
    `SIZE ${w} mm, ${h} mm`,
    `GAP ${gap} mm, 0`,
    'DIRECTION 1',
    'REFERENCE 0,0',
    'SET TEAR ON',
  ];

  const all = [];
  for (const part of header) {
    all.push(part);
  }

  for (const product of products) {
    const one = buildOneLabelCommands({
      settings,
      fieldLayout,
      product,
      getFieldValue,
      labelDpi: dpi,
    });
    for (const line of one) {
      all.push(line);
    }
  }

  const body = all.join('\r\n') + '\r\n';
  const buffer = encodeUtf8(body);
  return { ok: true, buffer };
}

/**
 * @param {object} settings
 * @returns {boolean}
 */
export function isTsplLayoutSupported(settings) {
  const cols = Math.max(1, parseInt(settings?.noOfColumns, 10) || 1);
  return cols === 1;
}
